import { mapProductToProductGridCardProps } from '@hyphengroup/cms-components/components/ProductGridCard';
import { getRewardCardGridItemsPerRow } from '@hyphengroup/cms-components/components/RewardCardGrid';
import {
  getRewardGridCardProductPreviewItems,
  getRewardGridCardValueText,
} from '@hyphengroup/cms-components/components/RewardGridCard';
import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import type { RewardCardGridTrackingEvent } from './types';
import type { ComponentRewardCardGridItem } from '$/(cms)/_modules/cmsService/types';
import { currencyFormat } from '$/components/cms/helpers/formatters';

export const getUniqueProducts = (
  products: ComponentRewardCardGridItem['products'],
) => {
  const deduplicatedProducts = new Map<
    string,
    NonNullable<typeof products>[number]
  >();

  (products ?? []).forEach((product) => {
    if (!product) {
      return;
    }

    const key =
      product.entryUID?.trim() ||
      product.uid?.trim() ||
      product.title?.trim() ||
      product.product_name?.trim();

    if (!key || deduplicatedProducts.has(key)) {
      return;
    }

    deduplicatedProducts.set(key, product);
  });

  return Array.from(deduplicatedProducts.values());
};

export const buildRewardCardsViewUrl = ({
  pathname,
  rewardId,
}: {
  pathname: string;
  rewardId: string;
}) => {
  const query = new URLSearchParams({
    view: 'cards',
    reward: rewardId,
  });

  return `${pathname}?${query.toString()}`;
};

export const buildRewardTrackingPayload = ({
  componentTitle,
  componentIndex,
  index,
  rewardCampaignName,
  rewardCampaignId,
}: {
  componentTitle: string;
  componentIndex: string;
  index: string;
  rewardCampaignName?: string;
  rewardCampaignId?: string;
}): RewardCardGridTrackingEvent => ({
  event: 'navigate',
  component_type: 'card',
  component_title: componentTitle,
  component_index: componentIndex,
  action: 'click',
  index,
  reward_campaign_name: rewardCampaignName,
  reward_campaign_id: rewardCampaignId,
});

export const buildRewardGridImpressionPayload = ({
  componentIndex,
}: {
  componentIndex: string;
}): RewardCardGridTrackingEvent => ({
  event: 'element_impression',
  component_type: 'card',
  component_title: 'Reward Card Grid',
  component_index: componentIndex,
  index: '',
});

export const getRewardGridItemIndex = ({
  index,
  layoutType,
  viewportWidth,
}: {
  index: number;
  layoutType: import('@hyphengroup/cms-components/components/RewardCardGrid').RewardCardGridLayoutType;
  viewportWidth?: number;
}) => {
  const resolvedViewportWidth =
    viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0);

  const itemsPerRow = getRewardCardGridItemsPerRow({
    layoutType,
    viewportWidth: resolvedViewportWidth,
  });
  const itemColumn = index % itemsPerRow;
  const itemRow = Math.floor(index / itemsPerRow);

  return `${itemColumn}.${itemRow}`;
};

export const getRewardValueText = (item: ComponentRewardCardGridItem) => {
  return getRewardGridCardValueText({
    gift: item.gift,
    formatCurrency: currencyFormat,
    formatNumber: currencyFormatterNoDecimal.format,
  });
};

export {
  getRewardGridCardProductPreviewItems,
  mapProductToProductGridCardProps,
};
